// L6 tramo 4 (ex L2 dual-run "sombra"). Compara, para cada fila de cartelería/hardware, la resolución
// del vínculo SII por la KEY LEGACY (posicional, `venta_tipo:ref_id:sii_ref_index`) contra la resolución
// por la KEY NUEVA (`linea:<linea_id>`, id estable de `lineas_facturacion`, L4) — la producción usa la
// key nueva desde este tramo; este auditor sigue viviendo en sombra pura para demostrar CERO
// divergencias entre ambos métodos mientras el cutover se termina de verificar.
//
// El loader la llama una vez por carga en dev y emite un único log.info con el resumen. No renderiza
// ni decide nada.
//
// Divergencia (método): una fila para la que la key LEGACY encuentra vínculo en `_byRefLegacy` pero la
// key NUEVA no encuentra el MISMO vínculo en `siiMap._byRef` (o viceversa). Esperado = 0.
//
// Excepción conocida (dato, no método): una fila HW-OPEX con vínculo → la factura quedó atribuida a la
// línea OPEX. Hoy son los 9 tramos de De La Industria (negocio 11, tras el reparto de L5), cuyo folio
// coincide con la gestión gemela; el mislink es de concepto (resuelto en L5), no un problema de método.
// El huérfano del puente (negocio 41, sin `meses_facturacion`) fue resuelto en L5 (línea hw 398).

import { refKeyDeOportunidad, refKeyLegacyDeOportunidad } from "./refKey";

const TIPOS_CARTELERIA = new Set(["Gestión", "HW-OPEX", "HW-CAPEX"]);

/**
 * @param {Array<Object>} filas - filas combinadas (se filtran las de cartelería/hardware).
 * @param {Object} siiMap - mapa SII de PRODUCCIÓN, con `_byRef` keyado por la key NUEVA (`linea:<id>`).
 * @param {Object|null} byRefLegacy - mapa dev-only keyado por la key LEGACY (posicional), la MISMA data
 *   que `siiMap._byRef` pero indexada por el método viejo. Si es null (producción), no audita.
 * @returns {{filas:number, conVinculo:number, divergencias:Array, excepcionesOpex:Array}}
 */
export const auditarDualRun = (filas, siiMap, byRefLegacy) => {
  const byRefNuevo = (siiMap && siiMap._byRef) || {};
  const legacy = byRefLegacy || {};
  const divergencias = [];
  const excepcionesOpex = [];
  let conVinculo = 0;
  let filasCart = 0;

  (filas || []).forEach((fila) => {
    if (!TIPOS_CARTELERIA.has(fila.type)) return;
    filasCart += 1;

    const keyLegacy = refKeyLegacyDeOportunidad(fila);
    const keyNueva = refKeyDeOportunidad(fila);
    const tieneVinculoLegacy = keyLegacy != null && Object.prototype.hasOwnProperty.call(legacy, keyLegacy);
    const tieneVinculoNuevo = keyNueva != null && Object.prototype.hasOwnProperty.call(byRefNuevo, keyNueva);

    if (!tieneVinculoLegacy && !tieneVinculoNuevo) return;
    conVinculo += 1;

    // Divergencia de MÉTODO: un lado encuentra vínculo y el otro no, o encuentran facturas distintas.
    const facturaLegacy = tieneVinculoLegacy ? legacy[keyLegacy]?.facturaId : null;
    const facturaNueva = tieneVinculoNuevo ? byRefNuevo[keyNueva]?.facturaId : null;
    if (tieneVinculoLegacy !== tieneVinculoNuevo || facturaLegacy !== facturaNueva) {
      divergencias.push({
        id: fila.id,
        negocio_id: fila.negocio_id,
        tipo: fila.type,
        ano: fila.ano,
        mes: fila.mes,
        keyLegacy,
        keyNueva,
        facturaLegacy,
        facturaNueva,
        motivo: "legacy_vs_linea_id_no_coinciden",
      });
    }

    if (fila.type === "HW-OPEX" && tieneVinculoNuevo) {
      excepcionesOpex.push({
        id: fila.id,
        negocio_id: fila.negocio_id,
        ano: fila.ano,
        mes: fila.mes,
        linea_id: fila.linea_id,
        keyNueva,
      });
    }
  });

  return { filas: filasCart, conVinculo, divergencias, excepcionesOpex };
};
